// ToastIntegration.js - Toast POS integration status and management
import React, { useState, useEffect, useCallback } from 'react';
import { getBaseURL } from '../config/api';
import axios from 'axios';


const api = axios.create({
  baseURL: `${getBaseURL()}/api`,
  withCredentials: true,
});

function ToastIntegration() {
  const [status, setStatus] = useState(null);
  const [config, setConfig] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [ordersPage, setOrdersPage] = useState(0);
  const [totalOrders, setTotalOrders] = useState(0);
  const PAGE_SIZE = 20;

  const fetchStatus = useCallback(async () => {
    try {
      const [statusRes, configRes] = await Promise.all([
        api.get('/admin/toast/status'),
        api.get('/admin/toast/config')
      ]);
      setStatus(statusRes.data);
      setConfig(configRes.data);
    } catch (err) {
      console.error('Error fetching Toast status:', err);
    }
  }, []);

  const fetchOrders = useCallback(async () => {
    try {
      const res = await api.get('/admin/toast/orders', {
        params: { limit: PAGE_SIZE, offset: ordersPage * PAGE_SIZE }
      });
      setOrders(res.data.orders);
      setTotalOrders(res.data.total);
    } catch (err) {
      console.error('Error fetching Toast orders:', err);
    }
  }, [ordersPage]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchStatus(), fetchOrders()]);
      setLoading(false);
    };
    loadData();
  }, [fetchStatus, fetchOrders]);

  // Poll for status updates while syncing
  useEffect(() => {
    if (syncing) {
      const interval = setInterval(() => {
        fetchStatus();
      }, 2000);
      return () => clearInterval(interval);
    }
  }, [syncing, fetchStatus]);

  // Update syncing state based on status
  useEffect(() => {
    if (status?.syncStatus === 'idle' || status?.syncStatus === 'error') {
      setSyncing(false);
    }
  }, [status?.syncStatus]);

  const handleSync = async () => {
    setSyncing(true);
    setError(null);
    try {
      await api.post('/admin/toast/sync');
      // The sync runs in background, status will update via polling
    } catch (err) {
      setError('Failed to start sync');
      setSyncing(false);
    }
  };

  const handleTestConnection = async () => {
    try {
      const res = await api.post('/admin/toast/test-connection');
      alert(res.data.message);
    } catch (err) {
      alert(err.response?.data?.error || 'Connection test failed');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleString();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  if (loading) {
    return <div className="p-4 text-center">Loading Toast integration...</div>;
  }

  return (
    <div className="container mx-auto p-4 max-w-6xl">
      <h1 className="text-2xl font-bold mb-6">Toast POS Integration</h1>

      {/* Configuration Status */}
      <div className="bg-gray-800 rounded p-4 mb-6">
        <h2 className="text-xl font-semibold mb-4">Configuration Status</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center gap-2">
            <span className={`w-3 h-3 rounded-full ${config?.isConfigured ? 'bg-green-500' : 'bg-red-500'}`}></span>
            <span>Overall Status: {config?.isConfigured ? 'Configured' : 'Not Configured'}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className={`w-3 h-3 rounded-full ${config?.hasClientId ? 'bg-green-500' : 'bg-red-500'}`}></span>
            <span>Client ID: {config?.hasClientId ? 'Set' : 'Missing'}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className={`w-3 h-3 rounded-full ${config?.hasClientSecret ? 'bg-green-500' : 'bg-red-500'}`}></span>
            <span>Client Secret: {config?.hasClientSecret ? 'Set' : 'Missing'}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className={`w-3 h-3 rounded-full ${config?.hasRestaurantGuid ? 'bg-green-500' : 'bg-red-500'}`}></span>
            <span>Restaurant GUID: {config?.hasRestaurantGuid ? 'Set' : 'Missing'}</span>
          </div>
        </div>

        {!config?.isConfigured && (
          <div className="mt-4 p-3 bg-yellow-900 rounded text-yellow-200">
            <p className="font-semibold">Configuration Required</p>
            <p className="text-sm mt-1">
              Set the following environment variables in your backend .env file:
            </p>
            <ul className="text-sm mt-2 list-disc list-inside">
              <li>TOAST_CLIENT_ID</li>
              <li>TOAST_CLIENT_SECRET</li>
              <li>TOAST_RESTAURANT_GUID</li>
              <li>TOAST_API_HOSTNAME (optional, defaults to api.toasttab.com)</li>
            </ul>
          </div>
        )}

        {config?.isConfigured && (
          <div className="mt-4">
            <button
              onClick={handleTestConnection}
              className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded"
            >
              Test Connection
            </button>
          </div>
        )}
      </div>

      {/* Sync Status */}
      <div className="bg-gray-800 rounded p-4 mb-6">
        <h2 className="text-xl font-semibold mb-4">Sync Status</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <div className="bg-gray-700 rounded p-3">
            <div className="text-gray-400 text-sm">Status</div>
            <div className={`text-lg font-semibold ${
              status?.syncStatus === 'running' ? 'text-yellow-400' :
              status?.syncStatus === 'error' ? 'text-red-400' :
              'text-green-400'
            }`}>
              {status?.syncStatus === 'running' ? 'Syncing...' :
               status?.syncStatus === 'error' ? 'Error' :
               'Idle'}
            </div>
          </div>
          <div className="bg-gray-700 rounded p-3">
            <div className="text-gray-400 text-sm">Last Sync</div>
            <div className="text-lg font-semibold">{formatDate(status?.lastSyncAt)}</div>
          </div>
          <div className="bg-gray-700 rounded p-3">
            <div className="text-gray-400 text-sm">Total Orders Processed</div>
            <div className="text-lg font-semibold">{status?.ordersProcessed || 0}</div>
          </div>
          <div className="bg-gray-700 rounded p-3">
            <div className="text-gray-400 text-sm">Auto-Sync</div>
            <div className="text-lg font-semibold">{config?.isConfigured ? 'Every 5 min' : 'Disabled'}</div>
          </div>
        </div>

        {status?.errorMessage && (
          <div className="mb-4 p-3 bg-red-900 rounded text-red-200">
            <p className="font-semibold">Error:</p>
            <p className="text-sm">{status.errorMessage}</p>
          </div>
        )}

        {error && (
          <div className="mb-4 p-3 bg-red-900 rounded text-red-200">
            <p>{error}</p>
          </div>
        )}

        <button
          onClick={handleSync}
          disabled={!config?.isConfigured || syncing}
          className={`px-4 py-2 rounded ${
            !config?.isConfigured || syncing
              ? 'bg-gray-600 cursor-not-allowed'
              : 'bg-green-600 hover:bg-green-700'
          }`}
        >
          {syncing ? 'Syncing...' : 'Sync Now'}
        </button>
      </div>

      {/* Recent Orders */}
      <div className="bg-gray-800 rounded p-4">
        <h2 className="text-xl font-semibold mb-4">Recent Toast Orders</h2>

        {orders.length === 0 ? (
          <p className="text-gray-400">No orders synced yet.</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="px-4 py-3 text-left">Customer</th>
                    <th className="px-4 py-3 text-left">Phone</th>
                    <th className="px-4 py-3 text-left">Type</th>
                    <th className="px-4 py-3 text-left">Total</th>
                    <th className="px-4 py-3 text-left">Items</th>
                    <th className="px-4 py-3 text-left">Processed At</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => {
                    const items = typeof order.items === 'string' ? JSON.parse(order.items) : order.items;
                    return (
                      <tr key={order.id} className="border-b border-gray-700 hover:bg-gray-750">
                        <td className="px-4 py-3">{order.customer_name}</td>
                        <td className="px-4 py-3">{order.customer_phone || '-'}</td>
                        <td className="px-4 py-3">{order.order_type}</td>
                        <td className="px-4 py-3">{formatCurrency(order.order_total)}</td>
                        <td className="px-4 py-3">
                          <div className="max-w-xs truncate text-sm text-gray-400">
                            {items?.map(i => i.name).join(', ') || '-'}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm">{formatDate(order.processed_at)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="mt-4 flex justify-between items-center">
              <span className="text-gray-400 text-sm">
                Showing {ordersPage * PAGE_SIZE + 1} - {Math.min((ordersPage + 1) * PAGE_SIZE, totalOrders)} of {totalOrders}
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setOrdersPage(p => Math.max(0, p - 1))}
                  disabled={ordersPage === 0}
                  className={`px-3 py-1 rounded ${ordersPage === 0 ? 'bg-gray-700 text-gray-500' : 'bg-gray-600 hover:bg-gray-500'}`}
                >
                  Previous
                </button>
                <button
                  onClick={() => setOrdersPage(p => p + 1)}
                  disabled={(ordersPage + 1) * PAGE_SIZE >= totalOrders}
                  className={`px-3 py-1 rounded ${(ordersPage + 1) * PAGE_SIZE >= totalOrders ? 'bg-gray-700 text-gray-500' : 'bg-gray-600 hover:bg-gray-500'}`}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default ToastIntegration;
